import React, { useState, useMemo } from "react";
import { Clock, TrendingUp, Sparkles, ArrowRight, Rocket } from "lucide-react";
import { track } from "@/lib/analytics";

// CEFR hour standards (Council of Europe, conservative for Brazilian PT speakers
// benefiting from Latin cognates on reading & vocab).
const CUMULATIVE_HOURS = {
  A1: 100,
  A2: 250,
  B1: 500,
  B2: 800,
  C1: 1100,
  C2: 1400,
};

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

const COPY = {
  pt: {
    overline: "Calculadora de fluência",
    intro: "Quanto tempo até seu nível-alvo?",
    hoursLabel: "Quantas horas por semana você estuda hoje?",
    hoursHint: "Conte tudo: app, série, aula, leitura, podcast.",
    targetLabel: "Qual seu nível-alvo?",
    withoutLabel: "Sua rotina atual",
    withLabel: "Com 2 aulas comigo/semana",
    unit: "meses",
    unitSingular: "mês",
    savedByMe: "economia com meu método",
    ctaTitle: "Quer chegar em {level} em {months}?",
    ctaSub: "Marca aula grátis. Em 50 min traçamos teu plano.",
    cta: "Quero minha aula grátis",
    disclaimer: "* Estimativa baseada no padrão CEFR (Council of Europe) + eficiência 1.6x comprovada em aulas guiadas com feedback direto. Resultado real varia por dedicação e ponto de partida.",
    reachedTitle: "Você já chegou no {level} 🎉",
    reachedSub: "Marca aula grátis pra descobrir seu próximo alvo (C1 ou C2) e o plano pra chegar lá.",
  },
  en: {
    overline: "Fluency calculator",
    intro: "How long to your target level?",
    hoursLabel: "How many hours per week do you study now?",
    hoursHint: "Count everything: app, series, lessons, reading, podcasts.",
    targetLabel: "Your target level?",
    withoutLabel: "Your current routine",
    withLabel: "With 2 lessons with me/week",
    unit: "months",
    unitSingular: "month",
    savedByMe: "saved with my method",
    ctaTitle: "Want to reach {level} in {months}?",
    ctaSub: "Book a free class. In 50 min we map your plan.",
    cta: "Book my free class",
    disclaimer: "* CEFR (Council of Europe) standard + 1.6x efficiency observed in guided classes with direct feedback. Actual result varies by dedication and starting point.",
    reachedTitle: "You've already reached {level} 🎉",
    reachedSub: "Book a free class to identify your next target (C1 or C2) and how to get there.",
  },
  fr: {
    overline: "Calculateur de fluidité",
    intro: "Combien de temps jusqu'à ton niveau-cible ?",
    hoursLabel: "Combien d'heures par semaine tu étudies aujourd'hui ?",
    hoursHint: "Compte tout : app, série, cours, lecture, podcast.",
    targetLabel: "Ton niveau-cible ?",
    withoutLabel: "Ta routine actuelle",
    withLabel: "Avec 2 cours avec moi/semaine",
    unit: "mois",
    unitSingular: "mois",
    savedByMe: "économisé avec ma méthode",
    ctaTitle: "Envie d'atteindre {level} en {months} ?",
    ctaSub: "Réserve un cours gratuit. En 50 min on trace ton plan.",
    cta: "Je réserve mon cours",
    disclaimer: "* Basé sur le standard CEFR (Conseil de l'Europe) + efficacité 1.6x observée en cours guidés avec feedback direct.",
    reachedTitle: "Tu as déjà atteint {level} 🎉",
    reachedSub: "Réserve un cours gratuit pour identifier ta prochaine cible (C1 ou C2).",
  },
};

// Compute months to go from `currentLevel` to `targetLevel` at `weeklyHours` per week.
// With me: adds +3 productive hours/week (2h class + 1h focused homework)
//          AND applies 1.6x efficiency multiplier on those hours (targeted).
const monthsToLevel = (currentLevel, targetLevel, weeklyHours) => {
  const startH = CUMULATIVE_HOURS[currentLevel] ?? 0;
  const targetH = CUMULATIVE_HOURS[targetLevel] ?? 0;
  const remaining = Math.max(0, targetH - startH);
  if (remaining === 0 || weeklyHours <= 0) return { without: 0, withMe: 0 };
  const weeksWithout = remaining / weeklyHours;
  const withMeEffective = weeklyHours + 3 * 1.6; // extra 4.8 effective hours
  const weeksWith = remaining / withMeEffective;
  return {
    without: Math.round((weeksWithout / 4.33) * 10) / 10,
    withMe: Math.round((weeksWith / 4.33) * 10) / 10,
  };
};

const nextLevel = (level) => {
  const idx = LEVELS.indexOf(level);
  if (idx === -1 || idx >= LEVELS.length - 1) return null;
  // Default target = current + 2 (so A2 → B2, B1 → C1, etc). Cap at C2.
  return LEVELS[Math.min(idx + 2, LEVELS.length - 1)];
};

const FluencyCalculator = ({ currentLevel, lang }) => {
  const t = COPY[lang] || COPY.pt;
  const defaultTarget = nextLevel(currentLevel) || "C2";
  const [hours, setHours] = useState(3);
  const [target, setTarget] = useState(defaultTarget);
  const [tracked, setTracked] = useState(false);

  const result = useMemo(
    () => monthsToLevel(currentLevel, target, hours),
    [currentLevel, target, hours]
  );

  // Target level is already reached
  const alreadyReached =
    LEVELS.indexOf(target) <= LEVELS.indexOf(currentLevel);

  const savedMonths = Math.max(0, Math.round((result.without - result.withMe) * 10) / 10);
  const savedPct = result.without > 0
    ? Math.round(((result.without - result.withMe) / result.without) * 100)
    : 0;

  const availableTargets = LEVELS.slice(
    Math.max(1, LEVELS.indexOf(currentLevel) + 1)
  );

  const onCtaClick = () => {
    if (!tracked) {
      track("fluency_calc_cta_click", {
        current_level: currentLevel,
        target,
        weekly_hours: hours,
        months_without: result.without,
        months_with_me: result.withMe,
      });
      setTracked(true);
    }
  };

  const fmtMonths = (n) => {
    if (n === 0) return `< 1 ${t.unitSingular}`;
    if (n < 1) return `~1 ${t.unitSingular}`;
    return `${n.toFixed(1).replace(".0", "")} ${t.unit}`;
  };

  return (
    <section
      data-testid="fluency-calculator"
      className="mt-16 bg-white border border-[#e5e3db] rounded-2xl overflow-hidden shadow-[0_24px_60px_-30px_rgba(30,42,90,0.25)]"
    >
      <div className="p-6 sm:p-10">
        <p className="overline flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-[#d9381e]" strokeWidth={2} />
          {t.overline}
        </p>
        <h3 className="font-display mt-3 text-2xl sm:text-3xl tracking-tight text-[#1e2a5a]">
          {t.intro}
        </h3>

        {/* Inputs */}
        <div className="mt-8 grid sm:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="fc-hours"
              className="block text-xs uppercase tracking-[0.18em] text-[#5c5c5c] mb-2"
            >
              <Clock className="inline h-3.5 w-3.5 -mt-0.5 mr-1 text-[#d9381e]" />
              {t.hoursLabel}
            </label>
            <div className="flex items-baseline gap-3">
              <input
                id="fc-hours"
                type="range"
                min="1"
                max="20"
                value={hours}
                onChange={(e) => setHours(Number(e.target.value))}
                data-testid="fluency-hours-input"
                className="flex-1 accent-[#d9381e]"
              />
              <span
                className="font-display text-3xl text-[#1e2a5a] tabular-nums w-14 text-right"
                data-testid="fluency-hours-value"
              >
                {hours}h
              </span>
            </div>
            <p className="mt-2 text-xs text-[#5c5c5c] italic">{t.hoursHint}</p>
          </div>

          <div>
            <label
              htmlFor="fc-target"
              className="block text-xs uppercase tracking-[0.18em] text-[#5c5c5c] mb-2"
            >
              <TrendingUp className="inline h-3.5 w-3.5 -mt-0.5 mr-1 text-[#d9381e]" />
              {t.targetLabel}
            </label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {(availableTargets.length ? availableTargets : ["C2"]).map((lv) => (
                <button
                  key={lv}
                  type="button"
                  onClick={() => setTarget(lv)}
                  data-testid={`fluency-target-${lv}`}
                  className={`rounded-full py-2 text-sm font-semibold transition-all border ${
                    target === lv
                      ? "bg-[#1e2a5a] text-[#f4f1ea] border-[#1e2a5a]"
                      : "border-[#e5e3db] text-[#5c5c5c] hover:border-[#1e2a5a] hover:text-[#1e2a5a]"
                  }`}
                >
                  {lv}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Comparison — reveals only when target is above current */}
        {!alreadyReached && (
          <>
            <div className="mt-10 grid md:grid-cols-2 gap-4">
              {/* Without me */}
              <div className="rounded-xl border border-[#e5e3db] bg-[#f0efea] p-6">
                <p className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c]">
                  {t.withoutLabel}
                </p>
                <p
                  className="mt-3 font-display text-4xl sm:text-5xl text-[#5c5c5c] tabular-nums"
                  data-testid="fluency-without"
                >
                  {fmtMonths(result.without)}
                </p>
              </div>
              {/* With me */}
              <div className="rounded-xl border-2 border-[#d9381e] bg-[#d9381e]/5 p-6 relative">
                <span className="absolute -top-3 left-5 rounded-full bg-[#d9381e] text-[#f4f1ea] px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-semibold">
                  <Rocket className="inline h-3 w-3 mr-1 -mt-0.5" />
                  Prof Lionel
                </span>
                <p className="text-xs uppercase tracking-[0.18em] text-[#d9381e]">
                  {t.withLabel}
                </p>
                <p
                  className="mt-3 font-display text-4xl sm:text-5xl text-[#d9381e] tabular-nums"
                  data-testid="fluency-with-me"
                >
                  {fmtMonths(result.withMe)}
                </p>
                {savedPct >= 10 && (
                  <p className="mt-2 text-xs text-[#3a3a3a]">
                    <strong className="text-[#1e7a3e]">−{savedPct}%</strong> {t.savedByMe}
                  </p>
                )}
              </div>
            </div>

            {/* CTA */}
            <div className="mt-10 rounded-xl bg-[#1e2a5a] text-[#f4f1ea] p-6 sm:p-8 flex flex-wrap items-center gap-5 justify-between">
              <div className="flex-1 min-w-[240px]">
                <p className="font-display text-lg sm:text-xl">
                  {t.ctaTitle
                    .replace("{level}", target)
                    .replace("{months}", fmtMonths(result.withMe))}
                </p>
                <p className="mt-2 text-sm text-[#f4f1ea]/75">{t.ctaSub}</p>
              </div>
              <a
                href="/aula-gratis"
                onClick={onCtaClick}
                data-testid="fluency-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-[#d9381e] px-6 py-3 text-sm font-semibold transition-colors hover:bg-[#f4f1ea] hover:text-[#1e2a5a]"
              >
                {t.cta}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </>
        )}

        {/* If already reached target */}
        {alreadyReached && (
          <div
            data-testid="fluency-already-reached"
            className="mt-10 rounded-xl bg-[#e8f4ea] border border-[#1e7a3e]/25 p-6"
          >
            <p className="font-display text-xl text-[#1e2a5a]">
              {t.reachedTitle.replace("{level}", target)}
            </p>
            <p className="mt-2 text-sm text-[#3a3a3a]">{t.reachedSub}</p>
            <a
              href="/aula-gratis"
              onClick={onCtaClick}
              data-testid="fluency-cta"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#d9381e] px-5 py-2.5 text-sm font-semibold text-[#f4f1ea] hover:bg-[#1e2a5a] transition-colors"
            >
              {t.cta}
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        )}

        <p className="mt-6 text-[11px] text-[#5c5c5c] italic leading-relaxed">
          {t.disclaimer}
        </p>
      </div>
    </section>
  );
};

export default FluencyCalculator;
